import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  listFraisTransportConfig, 
  updateFraisTransportConfig, 
  createFraisTransportConfig, 
  deleteFraisTransportConfig 
} from "../services/api";
import "../components/PrixOuvrageForm.css";

const defaultCharges = [
  "Camion",
  "Assurance véhicule", 
  "Carburant"
];

const FraisTransportConfigPage = () => {
  const navigate = useNavigate();

  const [charges, setCharges] = useState([]);
  const [customLabel, setCustomLabel] = useState("");
  const [customBudget, setCustomBudget] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    loadCharges();
  }, []);

  const loadCharges = async () => {
    try {
      const data = await listFraisTransportConfig();
      // Ensure all default charges exist
      const existingNames = data.map(item => item.name);
      const missingCharges = defaultCharges.filter(charge => !existingNames.includes(charge));
      
      // Create missing default charges
      for (const chargeName of missingCharges) {
        await createFraisTransportConfig({ name: chargeName, prix: 0 });
      }
      
      // Reload data
      const updatedData = await listFraisTransportConfig();
      setCharges(updatedData);
    } catch (err) {
      setError("Erreur de chargement");
    }
  };

  const handleChargeChange = (id, value) => {
    setCharges(charges.map(charge => 
      charge.id === id ? { ...charge, prix: Number(value) } : charge
    ));
    setError("");
  };

  const addCustomCharge = async () => {
    if (!customLabel || !customBudget) return;
    try {
      const newCharge = await createFraisTransportConfig({
        name: customLabel,
        prix: Number(customBudget)
      });
      setCharges([...charges, newCharge]);
      setCustomLabel("");
      setCustomBudget("");
    } catch (err) {
      setError("Erreur lors de l'ajout de la charge");
    }
  };

  const removeCustomCharge = async (id) => {
    try {
      await deleteFraisTransportConfig(id);
      setCharges(charges.filter(charge => charge.id !== id));
    } catch (err) {
      setError("Erreur lors de la suppression");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      // Update all charges
      for (const charge of charges) {
        await updateFraisTransportConfig(charge.id, {
          name: charge.name,
          prix: Number(charge.prix)
        });
      }
      navigate("/frais-transport");
    } catch (err) {
      setError("Erreur lors de la sauvegarde");
    } finally {
      setLoading(false);
    }
  };

  const isCustomCharge = (chargeName) => !defaultCharges.includes(chargeName);

  const calculateTotal = () => {
    const total = charges.reduce((total, charge) => total + Number(charge.prix || 0), 0);
    console.log('Transport config total calculated:', total); // Debug log
    return total;
  };

  return (
    <div className="prix-ouvrage-container">
      <form className="prix-ouvrage-form" onSubmit={handleSubmit}>
        <h2 className="prix-ouvrage-title">Configuration Frais de Transport</h2>
        <p className="prix-ouvrage-subtitle">
          Configurez les frais de transport pour le calcul des coûts. 
          <br />
          <small>
            Ce total sera proposé via le bouton "Ajouter frais de transport" lors de l'ajout des charges de personnel 
            pour un salarié possédant un camion. Aucune addition automatique n'est effectuée.
          </small>
        </p>

        {calculateTotal() > 0 && (
          <div className="total-display">
            <div className="total-amount">{calculateTotal().toFixed(2)} €</div>
            <div className="total-label">Total frais de transport</div>
          </div>
        )}

        <div className="form-section">
          <h3 className="section-title">
            <span className="section-icon">🚛</span>
            Frais de transport
          </h3>
          <div className="charges-grid">
            {charges.filter(charge => !isCustomCharge(charge.name)).map((charge) => (
              <div key={charge.id} className="charge-input-group">
                <label className="charge-label">{charge.name}</label>
                <input
                  type="number"
                  value={charge.prix}
                  onChange={(e) => handleChargeChange(charge.id, e.target.value)}
                  required
                  min="0"
                  step="0.01"
                  className="charge-input"
                  placeholder="0.00"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="form-section">
          <div className="add-charge-section">
            <h3 className="add-charge-title">
              <span className="section-icon">➕</span>
              Ajouter un frais personnalisé
            </h3>
            <div className="add-charge-form">
              <div className="add-charge-input">
                <label className="charge-label">Nom du frais</label>
                <input
                  type="text"
                  placeholder="Ex: Péage, Parking..."
                  value={customLabel}
                  onChange={(e) => setCustomLabel(e.target.value)}
                  className="charge-input"
                />
              </div>
              <div className="add-charge-input">
                <label className="charge-label">Montant (€)</label>
                <input
                  type="number"
                  placeholder="0.00"
                  value={customBudget}
                  onChange={(e) => setCustomBudget(e.target.value)}
                  className="charge-input"
                  min="0"
                  step="0.01"
                />
              </div>
              <button
                type="button"
                className="add-charge-button"
                onClick={addCustomCharge}
                disabled={!customLabel || !customBudget}
              >
                <span>➕</span>
                Ajouter
              </button>
            </div>
          </div>
        </div>

        {charges.filter(charge => isCustomCharge(charge.name)).length > 0 && (
          <div className="form-section">
            <div className="custom-charges-list">
              <h3 className="custom-charges-title">
                <span className="section-icon">📋</span>
                Frais personnalisés
              </h3>
              {charges.filter(charge => isCustomCharge(charge.name)).map((charge) => (
                <div key={charge.id} className="custom-charge-item">
                  <div className="custom-charge-info">
                    <span className="custom-charge-name">{charge.name}</span>
                    <input
                      type="number"
                      value={charge.prix}
                      onChange={(e) => handleChargeChange(charge.id, e.target.value)}
                      min="0"
                      step="0.01"
                      className="custom-charge-input"
                    />
                    <span>€</span>
                  </div>
                  <button
                    type="button"
                    className="delete-button"
                    onClick={() => removeCustomCharge(charge.id)}
                  >
                    🗑️ Supprimer
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {error && (
          <div className="error-message">
            <span>⚠️</span>
            {error}
          </div>
        )}

        <div className="form-actions">
          <button
            type="button"
            className="action-button cancel-button"
            onClick={() => navigate("/frais-transport")}
            disabled={loading}
          >
            Retour
          </button>
          <button 
            type="submit" 
            className="action-button save-button" 
            disabled={loading}
          >
            {loading && <span className="loading-spinner"></span>}
            {loading ? "Sauvegarde..." : "Sauvegarder"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FraisTransportConfigPage;
